import React, {useEffect} from 'react';
import {useCallback} from 'react';
import {useState} from 'react';
import {FieldTable} from '../../../components/FieldTable/FieldTable';
import {FieldTableColumnProps} from '../../../components/FieldTable/FieldTableTypes';
import {isoDateTimeToUk} from '../../../utils';
import FileSaver from 'file-saver';
import {
    useCreateCampaignSignatureMutation, useGetCampaignSignaturesQueryQuery,
    usePdfDownloadMutation
} from "../../../generated/graphql";
import {Alert, Button, ButtonGroup, Card, CardContent, CardHeader, TextField, Tooltip, Typography} from "@mui/material";
import {ModalWindow} from "../../../components/ModalWindow";
import {Cancel, Close, Download, Email, Web} from "@mui/icons-material";
import { useToast } from "../../../contexts/toast";
import {progressToast} from "../../../utils/progress-toast";

export const EContractDialog = ({reference, portfolio_hash, done, isShown, hasContactEmail, status, isDirty}) => {
    console.log('dialog', status, isDirty);
    const [email, setEmail] = useState('');
    const signatures = useGetCampaignSignaturesQueryQuery({
        variables: {
            ref: reference,
        },
        skip: !isShown,
        fetchPolicy: "network-only"
    });
    const [pdfDownloadFunc] = usePdfDownloadMutation();
    const [createSig] = useCreateCampaignSignatureMutation();
    const { toast } = useToast();

    const onOk = useCallback(async () => {
        const t = progressToast(toast)('SIGNATURE');
        try {
            t.start('Sending email');
            const result = await createSig({
                variables: {
                    campaign_id: signatures.data.campaign.id,
                    email: email
                }
            });
            if (!result.data.createCampaignSignature.id) {
                t.fail('Unable to send signature at this time');
                return;
            }
            t.ok('An E-Contract email has been sent to the contact');
            done(true);
        } catch (e) {
            t.fail(e.message?e.message:'Failed to send email');
        }
    }, [signatures, signatures.data, done, email, reference]);

    const onManual = useCallback(async () => {
        const t = progressToast(toast)('SIGNATURE');
        try {
            t.start('Generating signature link');
            const result = await createSig({
                variables: {
                    campaign_id: signatures.data.campaign.id,
                    is_manual: true,
                    email: ''
                }
            });
            if (!result.data.createCampaignSignature.id) {
                t.fail('Unable to generate a manual link for this contract');

                return;
            }
            const listener = (e) => {
                const str = '<a href="' + result.data.createCampaignSignature.graphqlContractUrl + '">' + reference + '</a>';
                e.clipboardData.setData("text/html", str);
                e.clipboardData.setData("text/plain", result.data.createCampaignSignature.graphqlContractUrl);
                e.preventDefault();
            }
            document.addEventListener("copy", listener);
            document.execCommand("copy");
            document.removeEventListener("copy", listener);
            t.ok('Manual link copied to clipboard');
            done(true);
        } catch (e) {
            t.fail(e.message ? e.message : 'Unable to generate link');
        }
    }, [signatures, signatures.data, done, email, reference]);


    const onPDF = useCallback(async () => {
        const t = progressToast(toast)('PDF_GEN');
        t.start('Downloading PDF');
        try {
            const data = await pdfDownloadFunc({
                variables: {
                    host: 'preview',
                    url: '/contract/' + reference + '/' + portfolio_hash + '/offline-form'
                }
            });
            t.ok('Rendering file');
            const res = await fetch(data.data.pdfDownload.data);
            const blobContent = await res.blob();
            FileSaver.saveAs(blobContent, reference + '.pdf');
            done(false);
        } catch (e) {
            t.fail(e.message ? e.message : 'Fetch failed');
        }
    }, [pdfDownloadFunc, reference, portfolio_hash]);
    const onCancel = useCallback(() => {
        done(false);
    }, [done]);

    useEffect(() => {
        if ((signatures && signatures.data && signatures.data.campaign.clientCompanyContact)) {
            setEmail(signatures.data.campaign.clientCompanyContact.email);
        }
    }, [signatures, signatures?.data]);

    let hasActiveContract = false;
    const changeEmail = (e) => {
        setEmail(e.target.value);
    };
    let manualLink: string = null;
    let signatureTable = null;
    if (signatures.data && signatures.data.campaign.campaignSignatures) {
        signatures.data.campaign.campaignSignatures.forEach((signature) => {
            if (signature.is_manual && signature.status != 'COMPLETE' && signature.status != 'CANCELLED') {
                manualLink = signature.graphqlContractUrl;
            }
        });
        const columns: FieldTableColumnProps<any>[] = [
            {
                accessor: 'createdBy.name',
                header: 'Created By',
                dataType:'default',
                readOnly:true
            },
            {
                accessor: 'status',
                header: 'Status',
                dataType:'default',
                readOnly:true
            },
            {
                accessor: 'email',
                options: {
                    renderViewer: ({row, field}) => {
                        if (row.is_manual) {
                            return '[manual distribution]'
                        }
                        return field.value;
                    }
                },
                header: 'Email',
                dataType:'default',
                readOnly:true
            },
            {
                accessor: 'created_at',
                header: 'Date/Time Sent',
                dataType: 'date',
                readOnly:true
            },
        ];
        signatureTable = <>
            <Typography>Previous E-Contracts</Typography>
            <FieldTable hasExport={false}
                                     className="affected-bookings__table" columns={columns} hasDelete={false}
                                     values={signatures.data.campaign.campaignSignatures}/>
        </>;
        hasActiveContract = true;
    } else {
        signatureTable = null;
    }

    const onManualCopy = useCallback(() => {
        const toastId = 'SIGNATURE';
        try {
            const listener = (e) => {
                const str = '<a href="' + manualLink + '">' + reference + '</a>';
                e.clipboardData.setData("text/html", str);
                e.clipboardData.setData("text/plain", manualLink);
                e.preventDefault();
            }
            document.addEventListener("copy", listener);
            document.execCommand("copy");
            document.removeEventListener("copy", listener);
            toast('Manual link copied to clipboard',{variant:'success'});
            done(true);
        } catch (e) {
            toast('Unable to generate link',{variant:'error'});
        }
    }, [signatures, signatures.data, done, email, manualLink, reference]);

    return (<ModalWindow
        isOpen={isShown}
        onClose={null}
        className="campaign__econtract-dialog"
        header={<div style={{display:'flex',alignItems:'center'}}><Email/> &nbsp; E-Contract </div>}
        actions={
            <Button variant="contained" onClick={onCancel} startIcon={<Close/>}>Close</Button>
        }><>
        {signatureTable}
        {isDirty ?
            <Typography>The campaign has changes outstanding, please save before generating a
                contract.</Typography> : null
        }
        {!isDirty && status != 'Quote' &&
            <Typography>Contracts may only be generated against campaigns
                in <b>Quote</b> status</Typography>
        }
        {!isDirty && status == 'Quote' &&
            <>
                        <Typography>The confirmation email must be sent to a single person who has the authorization at the
                            client to
                            confirm this campaign.</Typography>
                        {hasActiveContract && <Typography>
                            Caution: Sending a new contract will cancel any existing e-contract link.
                        </Typography>}
                        <TextField fullWidth={true} label="Single email address (do not use commas)" name="email" value={email}
                                   onChange={changeEmail}
                        />
                        <ButtonGroup sx={(theme)=>({paddingTop:theme.spacing(1),paddingBottom:theme.spacing(1)})}>
                    <Button variant="contained" onClick={onOk} startIcon={<Email/>}>Send E-Contract</Button>
                    {(manualLink == null) &&
                        <>{!hasContactEmail &&
                            <Tooltip
                                title="To generate a manual link, the client contact for this campaign must have an email address">
                                <Button variant="contained" disabled={true} startIcon={<Web/>}>Generate link for manual
                                    distribution</Button>
                            </Tooltip>
                        }
                            {hasContactEmail &&
                                <Button variant="contained" onClick={onManual} startIcon={<Web/>}>Generate link for manual
                                    distribution</Button>
                            }
                        </>
                    }
                    </ButtonGroup>
                    {(manualLink != null) &&
                        <Button variant="contained" onClick={onManualCopy} startIcon={<Web/>}>Copy link for manual
                            distribution</Button>
                    }
                <Typography>Alternatively, if a customer cannot complete the online form, a PDF can be downloaded that must be
                    manually completed/scanned and returned.</Typography>
                    <ButtonGroup sx={(theme)=>({paddingTop:theme.spacing(1),paddingBottom:theme.spacing(1)})}>
                <Button variant="contained" onClick={onPDF} startIcon={<Download/>}>Download PDF</Button>
                </ButtonGroup>
                </>
        }
    </>
    </ModalWindow>);
};